import { Application, Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import {
  ARENA_BOTTOM,
  ARENA_TOP,
  SCENE_H,
  SCENE_W,
  queueSlotPos,
  type GameConfig,
  type GiftInfo,
  type TeamId,
} from '@rekrut/shared';
import type { Engine } from '../sim/engine.js';
import type { MatchState, Unit } from '../sim/state.js';
import { AvatarCache, loadCircleTexture } from './avatars.js';
import { Footer } from './footer.js';
import { PricePanel } from './pricepanel.js';
import { Hud } from './hud.js';
import { Overlays } from './overlays.js';
import { TextCache } from './text.js';
import { EXPLOSION_FRAMES, HP_STEPS, getTextures, type Tex } from './textures.js';
import type { Sound } from '../audio/sound.js';

/** Длительность анимации залпа у самого шара. */
const CAST_MS = 900;

/** Сколько надгробие стоит на месте гибели. */
const GRAVE_MS = 3000;

/** Длительность взрыва: сорок кадров исходной гифки. */
const BLAST_MS = 900;

/** Сколько воронок держим на арене одновременно. */
const SCORCH_LIMIT = 30;



/** Один боец на экране. Никаких масок: аватарка уже круглая. */
class UnitView {
  readonly root = new Container();
  readonly halo: Sprite;
  readonly avatar: Sprite;
  readonly hpTrack: Sprite;
  readonly hp: Sprite;
  readonly badge: Sprite;
  readonly crown: Sprite;
  /** Красная заливка поверх аватарки в момент залпа. */
  readonly castTint: Sprite;
  readonly name = new Sprite();

  constructor(tex: Tex, avatarTexture: Sprite['texture']) {
    this.halo = new Sprite(tex.disc);
    this.avatar = new Sprite(avatarTexture);
    // Дорожка и дуга — одна и та же геометрия, поэтому совпадают идеально.
    this.hpTrack = new Sprite(tex.hpArcs[HP_STEPS]);
    this.hp = new Sprite(tex.hpArcs[HP_STEPS]);
    this.badge = new Sprite(tex.levels[0]);
    this.crown = new Sprite(tex.crown);
    this.castTint = new Sprite(tex.disc);
    this.castTint.tint = 0xff2020;
    this.castTint.visible = false;
    for (const s of [this.halo, this.avatar, this.hpTrack, this.hp, this.badge, this.crown, this.castTint]) {
      s.anchor.set(0.5);
    }
    this.crown.visible = false;
    this.name.anchor.set(0.5, 1);
    this.root.addChild(
      this.halo,
      this.avatar,
      this.hpTrack,
      this.castTint,
      this.hp,
      this.badge,
      this.crown,
    );
  }

  destroy(): void {
    this.root.destroy({ children: true });
    this.name.destroy();
  }
}

interface Floater {
  sprite: Sprite;
  until: number;
}

interface Wave {
  sprite: Sprite;
  from: number;
  to: number;
  startedAt: number;
  duration: number;
}

interface Blast {
  sprite: Sprite;
  startedAt: number;
  size: number;
}

interface Grave {
  sprite: Sprite;
  startedAt: number;
  size: number;
}

interface Beam {
  g: Graphics;
  startedAt: number;
}

export class Renderer {
  readonly avatars = new AvatarCache(1600);
  readonly texts = new TextCache(900);

  private readonly tex: Tex;
  private readonly root = new Container();
  /**
   * Всё игровое поле в одном контейнере: в альбомном режиме он поворачивается
   * целиком, а симуляция остаётся вертикальной и ничего не знает про экран.
   *
   * Спрайты, которые обязаны стоять прямо — аватарки, ники, цифры, надгробия —
   * доворачиваются обратно на тот же угол. Повернуть всё скопом нельзя: лица
   * и текст легли бы на бок.
   */
  private readonly fieldRoot = new Container();
  /** Угол доворота для содержимого поля. 0 в портрете. */
  private upright = 0;
  private readonly bgLayer = new Graphics();
  /** Тень нейтральной полосы и кромки, у которых упираются армии. */
  private readonly frontShadow: Sprite;
  private readonly frontLayer = new Graphics();
  private readonly zoneLayer = new Graphics();
  private readonly throneLayer = new Container();
  private readonly queueLayer = new Container();
  private readonly projLayer = new Container();
  /**
   * Порядок земли снизу вверх: следы от взрывов → надгробия → всё живое.
   * Воронка не должна перекрывать могилу, которая появилась после неё.
   */
  private readonly scorchLayer = new Container();
  private readonly graveLayer = new Container();
  private readonly unitLayer = new Container();
  private readonly nameLayer = new Container();
  private readonly fxLayer = new Container();
  private readonly hud: Hud;
  private readonly footer: Footer;
  private readonly prices: PricePanel;
  private readonly overlays: Overlays;

  private readonly views = new Map<string, UnitView>();
  private readonly projSprites: Sprite[] = [];
  private readonly projTrails: Sprite[] = [];
  private readonly floaters: Floater[] = [];
  private readonly floaterPool: Sprite[] = [];
  private readonly waves: Wave[] = [];
  private readonly wavePool: Sprite[] = [];
  private readonly beams: Beam[] = [];
  private readonly scorches: Sprite[] = [];
  private readonly blasts: Blast[] = [];
  private readonly blastPool: Sprite[] = [];
  private readonly graves: Grave[] = [];
  private readonly gravePool: Sprite[] = [];
  private readonly queueViews: Sprite[] = [];
  private readonly queueCounters: Text[] = [];
  private readonly throneViews = new Map<TeamId, Container>();

  private shake = 0;
  private lastMatchId = '';
  private sound: Sound | null = null;
  private lastShotCount = 0;

  constructor(
    private readonly app: Application,
    private readonly engine: Engine,
    private readonly cfg: GameConfig,
  ) {
    this.tex = getTextures(cfg.unit.maxLevel);
    this.frontShadow = new Sprite(this.tex.seam);
    this.frontShadow.anchor.set(0.5);
    this.frontShadow.tint = 0x000000;
    this.hud = new Hud(engine);
    this.footer = new Footer();
    this.prices = new PricePanel(cfg, this.texts, this.tex);
    this.overlays = new Overlays(this.avatars);
    this.unitLayer.sortableChildren = true;

    this.fieldRoot.addChild(
      this.bgLayer,
      this.frontShadow,
      this.frontLayer,
      this.zoneLayer,
      this.throneLayer,
      this.scorchLayer,
      this.graveLayer,
      this.queueLayer,
      this.projLayer,
      this.unitLayer,
      this.nameLayer,
      this.fxLayer,
    );
    // Панели остаются вне поворота: текст должен читаться в любом режиме.
    this.root.addChild(this.fieldRoot, this.hud.view, this.footer.view, this.overlays.view);
    this.prices.view.zIndex = 5;
    this.root.addChild(this.prices.view);

    app.stage.addChild(this.root);

    for (let i = 0; i < cfg.unit.maxProjectiles; i++) {
      const s = new Sprite(this.tex.glow);
      s.anchor.set(0.5);
      s.visible = false;
      s.blendMode = 'add';
      this.projSprites.push(s);
      const t = new Sprite(this.tex.glow);
      t.anchor.set(0.5);
      t.visible = false;
      t.blendMode = 'add';
      this.projTrails.push(t);
      this.projLayer.addChild(t, s);
    }

    for (const team of ['A', 'B'] as TeamId[]) this.buildThrone(team);
    this.buildQueueSlots();
  }

  /**
   * Альбомный режим: поле кладётся набок и заполняет широкий кадр.
   *
   * Нужен для трансляции экрана в TikTok: в игровом режиме он заворачивает
   * вертикальную картинку телефона в горизонтальный кадр и добивает полосами.
   * Заполнить кадр можно, только если игра сама станет горизонтальной.
   */
  setLandscape(on: boolean): void {
    if (on) {
      // Логическая сцена 1080×1920 разворачивается в 1920×1080: ось марша
      // становится горизонтальной, троны разъезжаются влево и вправо.
      this.fieldRoot.rotation = -Math.PI / 2;
      this.fieldRoot.position.set(0, SCENE_W);
      this.upright = Math.PI / 2;
    } else {
      this.fieldRoot.rotation = 0;
      this.fieldRoot.position.set(0, 0);
      this.upright = 0;
    }
    this.hud.setLandscape(on);
    this.footer.setLandscape(on);
    this.prices.setLandscape(on);
    this.overlays.setLandscape(on);
  }

  /** Заказать аватарку заранее — до того, как боец окажется на поле. */
  preloadAvatar(userId: string, url: string): void {
    if (url) this.avatars.get(userId, url);
  }

  setSound(sound: Sound): void {
    this.sound = sound;
  }

  setGiftCatalog(catalog: GiftInfo[]): void {
    this.prices.setCatalog(catalog);
  }

  /**
   * Картинка внутри трона. Принимает любой URL, включая blob: от локального
   * файла, — стример подставляет лицо команды, не пересобирая проект.
   */
  setTeamImage(team: TeamId, url: string): void {
    const view = this.throneViews.get(team);
    if (!view) return;
    const portrait = view.children[2] as Sprite;
    if (!url) {
      portrait.visible = false;
      return;
    }
    portrait.texture = loadCircleTexture(url, 256);
    portrait.visible = true;
    this.cfg.teams[team].imageUrl = url;
  }

  render(dtMs: number): void {
    const s = this.engine.state;
    if (s.matchId !== this.lastMatchId) {
      this.lastMatchId = s.matchId;
      this.clearGround();
    }
    // Выстрелы озвучиваются по приросту счётчика: их сотни в секунду, и
    // отдельное событие на каждый забило бы всю очередь эффектов.
    if (this.sound) {
      const fired = s.stats.shotsFired - this.lastShotCount;
      this.lastShotCount = s.stats.shotsFired;
      if (fired > 0) this.sound.shots(fired);
      this.sound.tick(s.now);
    }
    this.drainFx(s);
    this.drawBackground(s);
    this.drawThrones(s);
    this.drawQueues(s);
    this.syncUnits(s);
    this.drawProjectiles(s);
    this.updateFloaters(dtMs);
    this.updateWaves(s);
    this.updateBeams(s);
    this.updateGraves(s);
    this.updateBlasts(s);
    this.hud.update(s, this.cfg);
    this.footer.update(s);
    this.prices.update();
    this.overlays.update(s);
    this.applyShake(dtMs);

    // Добиваем текстуры, у которых истекла отсрочка.
    this.avatars.sweep();
    this.texts.sweep();
  }

  // ─── фон, зоны, троны ────────────────────────────────────────────────────

  private drawBackground(s: MatchState): void {
    const g = this.bgLayer;
    g.clear();
    g.rect(0, 0, SCENE_W, SCENE_H).fill({ color: 0x05070c });

    const front = Math.max(ARENA_TOP, Math.min(ARENA_BOTTOM, s.frontY));
    g.rect(0, ARENA_TOP, SCENE_W, front - ARENA_TOP).fill({ color: s.teams.A.colorDark });
    g.rect(0, front, SCENE_W, ARENA_BOTTOM - front).fill({ color: s.teams.B.colorDark });

    this.drawFront(s, front);
    this.zoneLayer.clear();
  }

  /**
   * Линия фронта.
   *
   * Раньше она была видна только по стыку двух цветов и терялась: зритель не
   * понимал, где именно стоят армии и куда двигается перевес. Теперь у неё
   * две составляющие — мягкая тень провала между армиями и две кромки в
   * цветах сторон ровно там, где каждая упирается.
   */
  private drawFront(s: MatchState, front: number): void {
    const half = this.cfg.arena.bufferWidth / 2;

    // Тень: мягкий провал, читается как разрыв между строями.
    this.frontShadow.position.set(SCENE_W / 2, front);
    this.frontShadow.width = SCENE_W;
    this.frontShadow.height = this.cfg.arena.bufferWidth * 3.4;
    this.frontShadow.alpha = 0.55;

    const g = this.frontLayer;
    g.clear();

    // Кромки: дальше них сторона не пройдёт. Пульсируют, чтобы линия
    // не выглядела статичной разметкой.
    const pulse = 0.62 + Math.sin(s.now / 260) * 0.22;
    const topY = front - half;
    const botY = front + half;

    g.rect(0, topY - 26, SCENE_W, 26).fill({ color: s.teams.A.color, alpha: 0.12 });
    g.rect(0, botY, SCENE_W, 26).fill({ color: s.teams.B.color, alpha: 0.12 });

    g.moveTo(0, topY).lineTo(SCENE_W, topY).stroke({
      color: s.teams.A.color,
      width: 4,
      alpha: pulse,
    });
    g.moveTo(0, botY).lineTo(SCENE_W, botY).stroke({
      color: s.teams.B.color,
      width: 4,
      alpha: pulse,
    });

  }

  private buildThrone(team: TeamId): void {
    const c = new Container();
    const glow = new Sprite(this.tex.glow);
    glow.anchor.set(0.5);
    glow.blendMode = 'add';

    // Трон — это круг цвета команды, внутри которого стоит фото. Никаких
    // шестиугольников: фигура отвлекала от портрета, ради которого зритель
    // и выбирает сторону.
    const body = new Sprite(this.tex.disc);
    body.anchor.set(0.5);

    // Портрет команды внутри трона — лицо, ради которого выбирают сторону.
    const portrait = new Sprite();
    portrait.anchor.set(0.5);
    portrait.visible = false;

    // Здоровье и щит трона устроены ровно как у бойцов: кольцо вокруг.
    // Так зрителю не нужно учить два разных индикатора.
    const hpTrack = new Sprite(this.tex.hpArcs[HP_STEPS]);
    hpTrack.anchor.set(0.5);
    const hp = new Sprite(this.tex.hpArcs[HP_STEPS]);
    hp.anchor.set(0.5);
    const shield = new Sprite(this.tex.ring);
    shield.anchor.set(0.5);

    const label = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'system-ui, sans-serif',
        fontSize: 26,
        fontWeight: '800',
        fill: 0xffffff,
        stroke: { color: 0x05070c, width: 5 },
      }),
    });
    label.anchor.set(0.5);
    c.addChild(glow, body, portrait, hpTrack, hp, shield, label);
    this.throneLayer.addChild(c);
    this.throneViews.set(team, c);
  }

  private drawThrones(s: MatchState): void {
    for (const team of ['A', 'B'] as TeamId[]) {
      const th = s.thrones[team];
      const view = this.throneViews.get(team)!;
      const [glow, body, portrait, hpTrack, hp, shield, label] = view.children as [
        Sprite,
        Sprite,
        Sprite,
        Sprite,
        Sprite,
        Sprite,
        Text,
      ];
      const color = s.teams[team].color;
      const r = this.cfg.arena.throneRadius;

      for (const sp of [glow, body, portrait, hpTrack, hp, shield]) {
        sp.position.set(th.x, th.y);
        // Портрет команды и шкала жизни доворачиваются: в альбомном режиме
        // поле лежит набок, а лицо должно смотреть на зрителя прямо.
        sp.rotation = this.upright;
      }
      body.width = body.height = r * 2.12;
      body.tint = th.hitFlash > 0 ? 0xffffff : color;
      // Фото занимает весь трон, ободок цвета команды остаётся по краю.
      portrait.width = portrait.height = r * 1.9;

      glow.width = glow.height = r * (th.shielded ? 5 : 7);
      glow.tint = th.shielded ? color : 0xff3b3b;
      glow.alpha = 0.22 + th.hitFlash * 0.5 + (th.shielded ? 0 : 0.18 + Math.sin(s.now / 160) * 0.1);

      // Жизнь трона — кольцо вокруг него, как ободок HP у бойца.
      const frac = Math.max(0, Math.min(1, th.hp / th.maxHp));
      const arc = this.tex.hpArcs[Math.round(frac * HP_STEPS)]!;
      if (hp.texture !== arc) hp.texture = arc;
      hpTrack.width = hpTrack.height = r * 2.42;
      hpTrack.tint = color;
      hpTrack.alpha = 0.22;
      hp.width = hp.height = r * 2.42;
      hp.tint = th.hitFlash > 0 ? 0xffffff : color;

      // Щит — тоже кольцо, снаружи от полосы жизни.
      shield.visible = th.shielded;
      if (th.shielded) {
        shield.width = shield.height = r * 2.78;
        shield.tint = 0x9fe8ff;
        shield.alpha = 0.5 + Math.sin(s.now / 300) * 0.2;
      }

      label.position.set(th.x, team === 'A' ? th.y + r * 1.62 : th.y - r * 1.62);
      label.rotation = this.upright;
      const text = th.shielded ? `${Math.ceil(th.hp)}` : `ТРОН ОТКРЫТ  ${Math.ceil(th.hp)}`;
      if (label.text !== text) label.text = text;
      label.style.fill = th.shielded ? 0xffffff : 0xff5c5c;
    }
  }

  // ─── очередь ─────────────────────────────────────────────────────────────

  private buildQueueSlots(): void {
    const n = this.cfg.queue.visibleSlots;
    for (let i = 0; i < n * 2; i++) {
      const s = new Sprite();
      s.anchor.set(0.5);
      s.visible = false;
      this.queueViews.push(s);
      this.queueLayer.addChild(s);
    }
    for (let i = 0; i < 2; i++) {
      const t = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: 'system-ui, sans-serif',
          fontSize: 24,
          fontWeight: '800',
          fill: 0xffffff,
          stroke: { color: 0x05070c, width: 5 },
        }),
      });
      t.anchor.set(0, 0.5);
      this.queueCounters.push(t);
      this.queueLayer.addChild(t);
    }
  }

  /**
   * Очередь стоит колонной у самого края за своим троном и втягивается в бой
   * по мере освобождения мест. Видно первых visibleSlots, остальные — счётчиком.
   */
  private drawQueues(s: MatchState): void {
    const n = this.cfg.queue.visibleSlots;
    const size = 34;

    for (let t = 0; t < 2; t++) {
      const team: TeamId = t === 0 ? 'A' : 'B';
      const queue = s.teams[team].queue;

      for (let i = 0; i < n; i++) {
        const sprite = this.queueViews[t * n + i]!;
        const e = queue[i];
        if (!e) {
          sprite.visible = false;
          continue;
        }
        // Позиция берётся из общей функции: из этой же точки вылетает оружие
        // донатера, который ещё стоит в очереди.
        const pos = queueSlotPos(team, i, n);
        sprite.visible = true;
        sprite.texture = this.avatars.get(e.userId, e.avatarUrl);
        // Стрелок из очереди отыгрывает залп своей же аватаркой.
        const cast = this.castPulse(s.now, e.castAt);
        const rp = this.recoilPos(pos.x, pos.y, e.castDirX, e.castDirY, cast, 150);
        sprite.width = sprite.height = size * (1 + cast * 3);
        sprite.position.set(rp.x, rp.y);
        sprite.rotation = this.upright + e.castDirX * 0.3 * cast;
        // Ждущие возрождения — приглушены: видно, кто уже рвётся в бой,
        // а кто ещё отсчитывает свои секунды.
        sprite.alpha = e.readyAt > s.now ? 0.4 : e.priority ? 1 : 0.85;
      }

      const counter = this.queueCounters[t]!;
      const rest = Math.max(0, queue.length - n);
      const waiting = queue.filter((e) => e.readyAt > s.now).length;
      const text =
        queue.length === 0
          ? ''
          : rest > 0
            ? `+${rest} · ${waiting} на респауне`
            : `${waiting} на респауне`;
      if (counter.text !== text) counter.text = text;
      const last = queueSlotPos(team, n - 1, n);
      counter.position.set(last.x + 30, last.y);
      counter.rotation = this.upright;
      // В альбомном режиме очередь стоит вертикальной колонной у края кадра.
      // Счётчик команды B нужно писать влево, к центру поля: вправо ему
      // некуда — там край экрана и плашка с прайсом.
      counter.anchor.set(this.upright !== 0 && t === 1 ? 1 : 0, 0.5);
      counter.style.fill = s.teams[team].color;
    }
  }

  // ─── бойцы ───────────────────────────────────────────────────────────────

  private syncUnits(s: MatchState): void {
    for (const [id, view] of this.views) {
      if (s.units.has(id)) continue;
      view.destroy();
      this.views.delete(id);
    }

    let namesShown = 0;
    const maxNames = s.unitList.length > 260 ? 14 : 25;

    for (const u of s.unitList) {
      let v = this.views.get(u.userId);
      if (!v) {
        v = new UnitView(this.tex, this.avatars.get(u.userId, u.avatarUrl));
        this.views.set(u.userId, v);
        this.unitLayer.addChild(v.root);
        this.nameLayer.addChild(v.name);
      }

      // Отмечаем текстуру как нужную: боец живёт долго и к кэшу больше не
      // обращается, а без отметки его аватарка стареет и уходит под нож.
      this.avatars.touch(u.userId);

      const team = s.teams[u.team];
      const r = u.radius;

      // Залп отыгрывает сам шар: его отбрасывает отдачей против выстрела,
      // раздувает втрое и заливает красным, после чего он плавно
      // возвращается к своему размеру и продолжает бой.
      // Позицию НЕ подменяем: отдача уже отработана в симуляции, шар реально
      // стоит там, куда его отбросило. Рендер добавляет только раздувание,
      // красную заливку и наклон.
      const cast = this.castPulse(s.now, u.castAt);
      v.root.position.set(u.x, u.y);
      v.root.zIndex = cast > 0 ? 1e6 : u.y;
      v.root.rotation = this.upright + u.castDirX * 0.3 * cast;
      v.castTint.visible = cast > 0;
      if (cast > 0) {
        v.castTint.width = v.castTint.height = r * 1.72;
        v.castTint.alpha = cast * 0.55;
      }

      v.avatar.width = v.avatar.height = r * 1.72;

      // Цвет команды несёт ободок HP — самый заметный элемент шара. Уровень
      // читается размером, цифрой внутри и короной на десятом: цветного
      // кольца вокруг нет намеренно, оно замусоривало картинку.
      v.halo.width = v.halo.height = r * 1.98;
      v.halo.tint = team.color;
      v.halo.alpha = 0.35;

      const frac = Math.max(0, Math.min(1, u.hp / u.maxHp));
      const step = Math.round(frac * HP_STEPS);
      const arc = this.tex.hpArcs[step]!;
      if (v.hp.texture !== arc) v.hp.texture = arc;
      v.hp.width = v.hp.height = r * 2.05;
      v.hp.tint = team.color;
      v.hpTrack.width = v.hpTrack.height = r * 2.05;
      v.hpTrack.tint = team.color;
      v.hpTrack.alpha = 0.22;


      // Свежий уровень — короткая вспышка. Складывается с рывком залпа.
      const lvlAge = s.now - u.levelUpAt;
      const lvlPop = lvlAge < 350 ? 1 + (1 - lvlAge / 350) * 0.4 : 1;
      v.root.scale.set(lvlPop * (1 + cast * 2.1));

      v.avatar.tint = u.hitFlash > 0 ? mix(0xffffff, 0xff6b6b, u.hitFlash) : 0xffffff;
      if (s.now < u.invulnUntil) v.avatar.alpha = 0.6 + Math.sin(s.now / 60) * 0.2;
      else v.avatar.alpha = 1;

      // Цифра уровня — внизу внутри шара. Десять общих текстур на всё поле.
      const badge = this.tex.levels[u.level - 1]!;
      if (v.badge.texture !== badge) v.badge.texture = badge;
      v.badge.width = v.badge.height = r * 0.62;
      v.badge.position.set(0, r * 0.5);
      v.badge.visible = r > 12;

      const isKing = u.level >= this.cfg.unit.maxLevel;
      v.crown.visible = isKing || s.now < u.crownUntil;
      if (v.crown.visible) {
        v.crown.width = v.crown.height = r * 0.9;
        v.crown.position.set(0, -r - 10);
      }

      const wantName = isKing || s.now < u.showNameUntil;
      if (wantName && namesShown < maxNames) {
        namesShown++;
        const tt = this.texts.get(u.nickname, isKing ? 28 : 22);
        if (v.name.texture !== tt.texture) v.name.texture = tt.texture;
        v.name.visible = true;
        v.name.tint = isKing ? 0xffd25e : team.color;
        v.name.position.set(u.x, u.y - r - (isKing ? 24 : 10));
        v.name.rotation = this.upright;
      } else {
        v.name.visible = false;
      }
    }
  }

  private drawProjectiles(s: MatchState): void {
    for (let i = 0; i < this.projSprites.length; i++) {
      const p = s.projectiles[i];
      const sp = this.projSprites[i]!;
      const trail = this.projTrails[i]!;
      if (!p || !p.active) {
        sp.visible = false;
        trail.visible = false;
        continue;
      }
      sp.visible = true;
      sp.position.set(p.x, p.y);

      if (p.blastRadius > 0) {
        // Ракета: спрайт разворачивается носом по вектору скорости, сзади
        // горит факел двигателя.
        if (sp.texture !== this.tex.rocket) {
          sp.texture = this.tex.rocket;
          sp.blendMode = 'normal';
        }
        sp.height = 44;
        sp.width = 44 * (82 / 293);
        sp.tint = 0xffffff;
        sp.rotation = Math.atan2(p.vy, p.vx) + Math.PI / 2;

        const len = Math.hypot(p.vx, p.vy) || 1;
        trail.visible = true;
        trail.position.set(p.x - (p.vx / len) * 26, p.y - (p.vy / len) * 26);
        const flicker = 0.75 + Math.random() * 0.35;
        trail.width = trail.height = 30 * flicker;
        trail.tint = 0xffb03a;
        trail.alpha = 0.85 * flicker;
      } else {
        if (sp.texture !== this.tex.glow) {
          sp.texture = this.tex.glow;
          sp.blendMode = 'add';
        }
        sp.rotation = 0;
        sp.width = sp.height = p.tint ? 14 : 20;
        sp.tint = p.tint || s.teams[p.team].color;
        trail.visible = false;
      }
    }
  }

  // ─── эффекты ─────────────────────────────────────────────────────────────

  private drainFx(s: MatchState): void {
    for (const fx of s.fx) {
      switch (fx.kind) {
        case 'banner':
          this.overlays.showBanner(s.now, fx.title, fx.subtitle, fx.color, fx.ms);
          break;
        case 'damage':
          this.spawnFloater(s, fx.x, fx.y, `−${fx.amount}`, fx.big ? 34 : 24, fx.color);
          break;
        case 'shock':
          this.spawnWave(s, fx.x, fx.y, fx.radius, fx.color);
          break;
        case 'explosion':
          this.spawnScorch(fx.x, fx.y, fx.radius);
          this.spawnBlast(s, fx.x, fx.y, fx.radius);
          this.sound?.boom(s.now, Math.min(1.4, fx.radius / 220));
          break;
        case 'death':
          this.spawnWave(s, fx.x, fx.y, 46, fx.color);
          this.spawnGrave(s, fx.x, fx.y);
          break;
        case 'cast':
          this.spawnCast(s, fx.x, fx.y, fx.color);
          this.sound?.weapon(fx.sfx, s.now);
          break;
        case 'announce':
          this.sound?.announce(fx.id, s.now, this.cfg.announce.minGapMs);
          break;
        case 'levelup':
          this.spawnFloater(s, fx.x, fx.y - 30, `LV ${fx.level}`, 28, 0xffd25e);
          this.spawnWave(s, fx.x, fx.y, 70, 0xffd25e);
          this.sound?.levelUp();
          break;
        case 'laser':
          this.spawnBeam(s, fx.x1, fx.y1, fx.x2, fx.y2, fx.color, fx.width);
          break;
        case 'volleywave':
          this.sound?.blaster(s.now);
          break;
        case 'screenshake':
          this.shake = Math.max(this.shake, fx.power);
          break;
      }
    }
    s.fx.length = 0;
  }

  private spawnBeam(
    s: MatchState,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: number,
    width: number,
  ): void {
    const g = new Graphics();
    g.moveTo(x1, y1).lineTo(x2, y2).stroke({ color, width, alpha: 0.9 });
    g.moveTo(x1, y1).lineTo(x2, y2).stroke({ color: 0xffffff, width: width * 0.35, alpha: 1 });
    g.blendMode = 'add';
    this.fxLayer.addChild(g);
    this.beams.push({ g, startedAt: s.now });
  }

  private updateBeams(s: MatchState): void {
    for (let i = this.beams.length - 1; i >= 0; i--) {
      const b = this.beams[i]!;
      const t = (s.now - b.startedAt) / 420;
      if (t >= 1) {
        this.fxLayer.removeChild(b.g);
        b.g.destroy();
        this.beams.splice(i, 1);
        continue;
      }
      b.g.alpha = 1 - t;
    }
  }

  /**
   * Залп: вспышка и ударные волны в точке выстрела.
   *
   * Саму аватарку здесь НЕ рисуем. Копия автора, которую отбрасывает отдачей,
   * пока оригинал уезжает вперёд, читается как раздвоение — поэтому рывок,
   * раздувание и красную заливку применяет к своему же спрайту сам шар
   * (см. castPulse и его использование в syncUnits и drawQueues).
   */
  private spawnCast(s: MatchState, x: number, y: number, color: number): void {
    this.spawnWave(s, x, y, 150, color);
    this.spawnWave(s, x, y, 250, 0xffffff);
  }

  /**
   * Точка с учётом отдачи, прижатая к границам арены.
   *
   * Без ограничения аватарку из колонны очереди отбрасывало вверх за верхний
   * край арены — она уезжала под HUD, который рисуется поверх, и отдачи не
   * было видно вообще.
   */
  private recoilPos(
    x: number,
    y: number,
    dx: number,
    dy: number,
    cast: number,
    dist: number,
  ): { x: number; y: number } {
    const k = cast * dist;
    return {
      x: Math.max(60, Math.min(SCENE_W - 60, x + dx * k)),
      y: Math.max(ARENA_TOP + 60, Math.min(ARENA_BOTTOM - 60, y + dy * k)),
    };
  }

  /**
   * Профиль анимации залпа: быстрый рывок и плавный возврат ровно к единице.
   * Возвращает 0, когда анимации нет.
   */
  private castPulse(now: number, castAt: number): number {
    const t = (now - castAt) / CAST_MS;
    if (t < 0 || t >= 1) return 0;
    // Степень < 1 даёт резкий взлёт и мягкий спад — рывок, а не всплытие.
    return Math.sin(Math.PI * t ** 0.4);
  }

  /**
   * Воронка на месте взрыва. Живёт до конца раунда.
   *
   * Потолок в SCORCH_LIMIT штук: за матч ракет прилетают сотни, и без
   * ограничения арена превратилась бы в сплошное выжженное поле, на котором
   * не видно ни бойцов, ни границы фронта. Переполнение вытесняет самую
   * старую воронку.
   */
  private spawnScorch(x: number, y: number, radius: number): void {
    while (this.scorches.length >= SCORCH_LIMIT) {
      const old = this.scorches.shift()!;
      this.scorchLayer.removeChild(old);
      old.destroy();
    }
    const sp = new Sprite(this.tex.scorch);
    sp.anchor.set(0.5);
    sp.position.set(x, y);
    // Вчетверо меньше радиуса поражения: воронка — отметина на земле,
    // а не второй взрыв в полный размер.
    const size = radius * 0.52;
    sp.width = size;
    sp.height = size;
    // Развернём случайно, иначе серия ракет оставит одинаковые пятна.
    sp.rotation = Math.random() * Math.PI * 2;
    // Приглушаем: это выжженная земля, а не второй взрыв поверх поля.
    sp.alpha = 0.62;
    sp.tint = 0x9a8f88;
    this.scorchLayer.addChild(sp);
    this.scorches.push(sp);
  }

  /** Новый раунд — чистое поле. */
  private clearGround(): void {
    for (const sp of this.scorches) {
      this.scorchLayer.removeChild(sp);
      sp.destroy();
    }
    this.scorches.length = 0;
    for (const g of this.graves) {
      this.graveLayer.removeChild(g.sprite);
      this.gravePool.push(g.sprite);
    }
    this.graves.length = 0;
  }

  /**
   * Взрыв в точке попадания — кадры разобранной гифки.
   *
   * Рисуется аддитивно: чёрный фон исходника становится прозрачным сам собой,
   * а огонь начинает светиться поверх поля.
   */
  private spawnBlast(s: MatchState, x: number, y: number, radius: number): void {
    if (this.blasts.length >= 14) return;
    const sprite = this.blastPool.pop() ?? new Sprite(this.tex.explosion[0]);
    sprite.anchor.set(0.5);
    sprite.blendMode = 'add';
    sprite.position.set(x, y);
    sprite.rotation = Math.random() * Math.PI * 2;
    sprite.alpha = 1;
    sprite.visible = true;
    this.fxLayer.addChild(sprite);
    this.blasts.push({ sprite, startedAt: s.now, size: radius * 2.6 });
  }

  private updateBlasts(s: MatchState): void {
    for (let i = this.blasts.length - 1; i >= 0; i--) {
      const b = this.blasts[i]!;
      const t = (s.now - b.startedAt) / BLAST_MS;
      if (t >= 1) {
        b.sprite.visible = false;
        this.fxLayer.removeChild(b.sprite);
        this.blastPool.push(b.sprite);
        this.blasts.splice(i, 1);
        continue;
      }
      const frame = Math.min(EXPLOSION_FRAMES - 1, Math.floor(t * EXPLOSION_FRAMES));
      const tex = this.tex.explosion[frame]!;
      if (b.sprite.texture !== tex) b.sprite.texture = tex;
      // Кадры исходника 4:3, пропорцию сохраняем.
      b.sprite.width = b.size;
      b.sprite.height = b.size * 0.75;
    }
  }

  /**
   * Надгробие на месте гибели: три секунды на экране.
   *
   * Их держим ограниченным числом — при залпе гибнет десяток разом, и без
   * потолка поле превратилось бы в сплошное кладбище.
   */
  private spawnGrave(s: MatchState, x: number, y: number): void {
    if (this.graves.length >= 40) {
      // Самое старое уступает место свежему.
      const old = this.graves.shift()!;
      this.graveLayer.removeChild(old.sprite);
      this.gravePool.push(old.sprite);
    }
    const sprite = this.gravePool.pop() ?? new Sprite(this.tex.rip);
    sprite.texture = this.tex.rip;
    sprite.anchor.set(0.5, 0.82);
    sprite.rotation = this.upright;
    sprite.position.set(x, y);
    sprite.alpha = 1;
    sprite.visible = true;
    this.graveLayer.addChild(sprite);
    const size = 74 * this.engine.state.densityScale;
    this.graves.push({ sprite, startedAt: s.now, size });
  }

  private updateGraves(s: MatchState): void {
    for (let i = this.graves.length - 1; i >= 0; i--) {
      const g = this.graves[i]!;
      const t = (s.now - g.startedAt) / GRAVE_MS;
      if (t >= 1) {
        g.sprite.visible = false;
        this.graveLayer.removeChild(g.sprite);
        this.gravePool.push(g.sprite);
        this.graves.splice(i, 1);
        continue;
      }
      // Выскакивает из земли с перелётом, стоит, в конце тает.
      const pop = Math.min(1, t * 9);
      const ease = 1 - (1 - pop) ** 3;
      const overshoot = 1 + 0.18 * Math.sin(pop * Math.PI);
      g.sprite.height = g.size * ease * overshoot;
      g.sprite.width = g.size * ease * overshoot;
      g.sprite.alpha = t > 0.72 ? (1 - t) / 0.28 : 1;
    }
  }

  private spawnFloater(s: MatchState, x: number, y: number, text: string, size: number, color: number): void {
    if (this.floaters.length >= 28) return;
    const sprite = this.floaterPool.pop() ?? new Sprite();
    sprite.texture = this.texts.get(text, size, '900').texture;
    sprite.rotation = this.upright;
    sprite.anchor.set(0.5, 1);
    sprite.position.set(x, y);
    sprite.tint = color;
    sprite.alpha = 1;
    sprite.visible = true;
    this.fxLayer.addChild(sprite);
    this.floaters.push({ sprite, until: s.now + 850 });
  }

  private updateFloaters(dtMs: number): void {
    const now = this.engine.state.now;
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i]!;
      f.sprite.y -= (70 * dtMs) / 1000;
      f.sprite.alpha = Math.max(0, (f.until - now) / 850);
      if (now < f.until) continue;
      f.sprite.visible = false;
      this.fxLayer.removeChild(f.sprite);
      this.floaterPool.push(f.sprite);
      this.floaters.splice(i, 1);
    }
  }

  private spawnWave(s: MatchState, x: number, y: number, radius: number, color: number): void {
    if (this.waves.length >= 24) return;
    const sprite = this.wavePool.pop() ?? new Sprite(this.tex.ringThin);
    sprite.anchor.set(0.5);
    sprite.position.set(x, y);
    sprite.tint = color;
    sprite.alpha = 0.9;
    sprite.visible = true;
    sprite.blendMode = 'add';
    this.fxLayer.addChild(sprite);
    this.waves.push({ sprite, from: radius * 0.4, to: radius * 2, startedAt: s.now, duration: 420 });
  }

  private updateWaves(s: MatchState): void {
    for (let i = this.waves.length - 1; i >= 0; i--) {
      const w = this.waves[i]!;
      const t = (s.now - w.startedAt) / w.duration;
      if (t >= 1) {
        w.sprite.visible = false;
        this.fxLayer.removeChild(w.sprite);
        this.wavePool.push(w.sprite);
        this.waves.splice(i, 1);
        continue;
      }
      const r = w.from + (w.to - w.from) * t;
      w.sprite.width = w.sprite.height = r * 2;
      w.sprite.alpha = 0.9 * (1 - t);
    }
  }

  private applyShake(dtMs: number): void {
    if (this.shake <= 0.1) {
      this.root.position.set(0, 0);
      this.shake = 0;
      return;
    }
    this.root.position.set((Math.random() * 2 - 1) * this.shake, (Math.random() * 2 - 1) * this.shake);
    this.shake *= Math.max(0, 1 - dtMs / 260);
  }

  stats(): { views: number; avatars: number; texts: number } {
    return { views: this.views.size, avatars: this.avatars.size, texts: this.texts.size };
  }
}

function mix(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const r = Math.round(ar + (((b >> 16) & 255) - ar) * t);
  const g = Math.round(ag + (((b >> 8) & 255) - ag) * t);
  const bl = Math.round(ab + ((b & 255) - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

export type { Unit };
